import React, { useState } from "react";

import {NeedsLogin, NoLogin,NeedsProductionPermissions, NeedsSubscription, NeedsPermission, NeedsProductOrdersListPermission, NeedsAdminPermission, NeedsSuperAdminPermission} from "./auth";

import {
  Sliders,
  Users,
  Mail,
  Settings,
  Clipboard,
  UploadCloud,
  Video,
  Calendar
} from "react-feather";
import asyncComponent from "../components/Async";
import {signInPath, autoSignUpPath} from "./paths";
import {LocalStorage} from "../utils/storage";
import {Permissions, hasPermission, permissionTypes, hasAnyPermission} from "../components/permissions/permissions";
import async from "../components/Async";
import { planFeatures, PLAN_NAMES, PLAN_CAPABILITY, planIncludes, getPlanAvailableFromTooltip } from "../components/planFeatures/planFeatures";
import { FeatureFlagsService } from "../services/FeatureFlagsService";
import { getComponentLabels } from "../utils/languages";

// Auth components
export const SignIn = NoLogin(() => import("../pages/auth/SignIn"));
export const AutoSignUp = NoLogin(() => import("../pages/auth/AutoSignUp"));
const SignUp = NoLogin(() => import("../pages/auth/SignUp"));
const ResetPassword = NoLogin(() => import("../pages/auth/ResetPassword"));
const NewPassword = NoLogin(() => import("../pages/auth/NewPassword"));
const Page404 = NoLogin(() => import("../pages/auth/Page404"));
const Page500 = NoLogin(() => import("../pages/auth/Page500"));
const ChangePassword = NeedsLogin(() => import("../pages/auth/ChangePassword"));

// Dashboards components
const Default = NeedsPermission(() => import("../pages/dashboards/search"), Permissions['TRACEABILITY']);
const DefaultV2 = NeedsPermission(() => import("../pages/dashboards/search_v2"), Permissions['TRACEABILITY']);

// Home components
const Landing = NeedsLogin(() => import("../pages/landing/Landing"));
const Subscription = NeedsLogin(() => import("../pages/landing/Subscription"));
const Home = NeedsAdminPermission(() => import("../pages/home"));

// Pages components
const Blank = NeedsSubscription(() => import("../pages/pages/Blank"));

export const AsyncAddRawMaterial = asyncComponent(() => import("../pages/AddElement/rawMaterial"));
const AddRawMaterial = NeedsSubscription(() => import("../pages/AddElement/rawMaterial"));

export const AsyncAddEmployee = asyncComponent(() => import("../pages/AddElement/employee"));
export const AsyncAddSupplier = asyncComponent(() => import("../pages/AddElement/supplier"));
export const AsyncAddCustomer = asyncComponent(() => import("../pages/AddElement/customer"));
export const AsyncAddAlert = asyncComponent(() => import("../pages/AddElement/alert"));
export const AsyncAddProduct = asyncComponent(() => import("../pages/AddElement/product"));
export const AsyncAddSupply = asyncComponent(() => import("../pages/AddElement/supply"));
export const AsyncAddReception = asyncComponent(() => import("../pages/AddElement/reception"));
export const AsyncAddProductReception = asyncComponent(() => import("../pages/AddElement/productReception"));
export const AsyncAddSupplyReception = asyncComponent(() => import("../pages/AddElement/supplyReception"));
export const AsyncAddDistribution = asyncComponent(() => import("../pages/AddElement/distribution"));
export const AsyncAddProductOrder = asyncComponent(() => import("../pages/AddElement/productOrder"));
export const AsyncAddReprocess = asyncComponent(()=> import("../pages/AddElement/reprocess"));
export const AsyncAddProductionConfiguration = asyncComponent(() => import("../pages/productionConfigurations/ProductionConfiguration"));
export const AsyncAddProductStockDecrease = asyncComponent(() => import("../pages/AddElement/productStockDecrease"));
export const AsyncAddReprocessStockDecrease = asyncComponent(() => import("../pages/AddElement/reprocessStockDecrease"));
export const AsyncAddRawMaterialStockDecrease = asyncComponent(()=> import("../pages/AddElement/rawMaterialStockDecrease"));
export const AsyncAddSupplyStockDecrease = asyncComponent(()=> import("../pages/AddElement/supplyStockDecrease"));
export const AsyncAddProductBatch = asyncComponent(() => import("../pages/AddElement/productBatch"));
export const AsyncAddNonConformity = asyncComponent(() => import("../pages/AddElement/nonconformity"));
export const AsyncAddLocation = asyncComponent(() => import("../pages/AddElement/location"));
export const AsyncAddSupplyLocationEditable = asyncComponent(() => import("../pages/AddElement/supplyLocationEditable"));
export const AsyncAddProductLocationEditable = asyncComponent(() => import("../pages/AddElement/productLocationEditable"));
export const AsyncAddRawMaterialLocationEditable = asyncComponent(() => import("../pages/AddElement/rawMaterialLocationEditable"));

const Employees = NeedsPermission(() => import("../pages/employees/index"), Permissions['ORGANIZATION']);
const Organization = NeedsPermission(() => import("../pages/organizations/index"), Permissions['ORGANIZATION']);
const Customers = NeedsPermission(() => import("../pages/customers/index"), Permissions['CUSTOMERS']);
const RawMaterials = NeedsPermission(() => import("../pages/rawMaterials/index"), Permissions['RAW_MATERIAL_SETTINGS']);
const Formulas = NeedsPermission(() => import("../pages/formulas/index"), Permissions['FORMULAS']);
const Products = NeedsPermission(() => import("../pages/products/index"), Permissions['PRODUCT_SETTINGS']);
const Locations = NeedsPermission(() => import("../pages/locations/index"), Permissions['LOCATION_SETTINGS']);
const Supplies = NeedsPermission(() => import("../pages/supplies/index"), Permissions['SUPPLY_SETTINGS']);
const Suppliers = NeedsPermission(() => import("../pages/suppliers/index"), Permissions['SUPPLIERS']);
const Receptions = NeedsPermission(() => import("../pages/receptions/index"), Permissions['RAW_MATERIAL_RECEPTION']);
const SupplyReceptions = NeedsPermission(() => import("../pages/supplyReceptions/index"), Permissions['SUPPLY_RECEPTION']);
const SupplyStockDecreases = NeedsPermission(() => import("../pages/supplyStockDecreases/index"), Permissions['SUPPLY_STOCK_DECREASE']);
const Distributions = NeedsPermission(() => import("../pages/distributions/index"), Permissions['PRODUCT_DISTRIBUTION']);
const ProductOrders = NeedsProductOrdersListPermission(() => import("../pages/productOrders/index"));
const RawMaterialStockDecreases = NeedsPermission(() => import("../pages/rawMaterialStockDecreases/index"), Permissions['RAW_MATERIAL_STOCK_DECREASE']);
const ControlRecordsProductions = NeedsPermission(() => import("../pages/ControlRecordsProductions/index"), Permissions['CONTROL_RECORDS']);
const ProductBatches = NeedsPermission(() => import("../pages/productBatches/index"), Permissions['PRODUCT_STOCK'])
const RawMaterialBatches = NeedsPermission(() => import("../pages/rawMaterialBatches/index"), Permissions['RAW_MATERIAL_STOCK'])
const SupplyBatches = NeedsPermission(() => import("../pages/supplyBatches/index"), Permissions['SUPPLY_STOCK'])
const ReprocessesBatches = NeedsPermission(()=> import("../pages/reprocessesBatches/index"), Permissions['REPROCESS_STOCK'])
const ReprocessesStockDecreases = NeedsPermission(() => import("../pages/reprocessesStockDecreases/index"), Permissions['REPROCESS_STOCK']);
const GeneralSettings = NeedsPermission(() => import("../pages/settings/index"), Permissions['GENERAL_SETTINGS']);
const BarcodeLabelConfiguration = NeedsAdminPermission(() => import("../components/picking/barcodeConfiguration/BarcodeLabelConfiguration"));
const Alerts = NeedsPermission(() => import("../pages/alerts/index"), Permissions['ALERTS']); 
const KPIConfiguration = NeedsPermission(() => import("../pages/KPIConfiguration/index"), Permissions['KPI']); 
const ControlRecordsConfiguration = NeedsPermission(() => import("../pages/ControlRecordsConfiguration/index"), Permissions['CONTROL_RECORDS']);
const ControlRecords = NeedsPermission(() => import("../pages/ControlRecords/index"), Permissions['CONTROL_RECORDS']);
const CustomForms = NeedsPermission(() => import("../pages/customFormsConfiguration/index"), Permissions['CUSTOM_FORM_SETTINGS']);
const CustomForm = NeedsPermission(()=> import("../pages/customForm/index"), Permissions['CUSTOM_FORMS']);
const ProductStockDecreases  = NeedsPermission(() => import("../pages/productStockDecreases/index"), Permissions['PRODUCT_STOCK_DECREASE']); 
const ProductReceptions = NeedsPermission(() => import("../pages/productReceptions/index"), Permissions['PRODUCT_STOCK_DECREASE']);


//Register components

const Reception = NeedsPermission(() => import("../pages/RegisterForm/RegisterReception"), Permissions['RAW_MATERIAL_RECEPTION'], permissionTypes['WRITE']);
const Supply = NeedsPermission(() => import("../pages/RegisterForm/RegisterSupply"), Permissions['SUPPLY_RECEPTION'], permissionTypes['WRITE']);
const Distribution = NeedsPermission(() => import("../pages/RegisterForm/RegisterDistribution"), Permissions['PRODUCT_DISTRIBUTION'], permissionTypes['WRITE']);
const ProductOrder = NeedsPermission(() => import("../pages/RegisterForm/RegisterProductOrder"), Permissions['PRODUCT_ORDERS'], permissionTypes['WRITE']);
const BatchRequest = NeedsLogin(() => import("../pages/RegisterForm/RegisterBatchRequest"))
const Procedure = NeedsProductionPermissions(() => import("../pages/productions/ProductionContainer"));
const ConfigProductions = NeedsPermission(() => import("../pages/productionConfigurations/index"), Permissions['PRODUCTION_SETTINGS']);
const ProductionPlanningConfiguration = NeedsPermission(() => import("../pages/productionPlanning/index"), Permissions['PRODUCTION_PLANNING']);
const Planificaciones = NeedsPermission(() => import("../pages/productionPlanning/PlanificacionesPage"), Permissions['PRODUCTION_PLANNING']);
const Productions = NeedsProductionPermissions(() => import("../pages/productions/index"));
const Costs = NeedsProductionPermissions(() => import("../pages/costs/index"));
const FinalProductionStep = NeedsProductionPermissions(() => import("../pages/productions/FinalProductionStep"));
const RegisterProduction = NeedsPermission(() => import("../pages/productions/RegisterProduction"), Permissions['PRODUCTIONS']);
const ContactUs = NeedsLogin(() => import ("../pages/Contact/index"));
const MyCloud = NeedsPermission(() => import ("../pages/myCloud/index"), Permissions['CLOUD_STORAGE']);
const Nonconformities = NeedsLogin(() => import("../pages/nonconformities/index"));
const AllNonconformities = NeedsAdminPermission(() => import("../pages/nonconformities/index"));
const Admin = NeedsSuperAdminPermission(() => import("../pages/admin/index"));
const KPIStock = NeedsPermission(() => import("../pages/KPIStock/index"), Permissions['KPI']);
const KPIProductions = NeedsPermission(() => import("../pages/KPIProductions/index"), Permissions['KPI']);

const HealthCheck = async(() => import("../pages/healthCheck/index"))

const Implementation = async(() => import ("../pages/Implementation"))

const Notifications = NeedsLogin(() => import ("../pages/Notifications/index"))

const BatchRequests = NeedsLogin(() => import ("../pages/BatchRequests/index"))

export const authRoutes = {
  id: "Mi Cuenta",
  path: "/auth",
  icon: <Users />,
  children: [
    {
      id: "auto-sign-up",
      path: autoSignUpPath,
      name: "Crear cuenta",
      component: AutoSignUp,
      hidden: true
    },
    {
      id: "sign-in",
      path: signInPath,
      name: "Iniciar Sesión",
      component: SignIn,
      hidden: true
    },
    {
      path: "/auth/password/reset",
      name: "Olvidé mi Contraseña",
      component: ResetPassword,
      hidden: true
    },
    {
      path: "/auth/password/new/:token/:email",
      name: "Nueva Contraseña",
      component: NewPassword,
      hidden: true
    },
    {
      path: "/auth/sign-up",
      name: "Sign Up",
      component: SignUp,
      hidden: true
    },
    {
      path: "/auth/404",
      name: "404 Page",
      component: Page404,
      hidden: true
    },
    {
      path: "/auth/500",
      name: "500 Page",
      component: Page500,
      hidden: true
    },
    {
      path: "/health-check",
      name: "Health Check",
      component: HealthCheck,
      hidden: true
    }
  ]
};

const userRoutes = {
  id: "Mi Cuenta",
  path: "/user",
  icon: <Users />,
  hidden: true,
  children: [
    {
      id: "change-password",
      path: "/user/change-password",
      name: "Cambiar Contraseña",
      component: ChangePassword,
      hidden: true
    },
    {
      path: "/organizations",
      name: "Mi Empresa",
      component: Organization,
      hidden: true
    },
    {
      path: "/employees",
      name: "Mis Usuarios",
      component: Employees,
      hidden: true
    },
    {
      path: "/admin",
      name: "Admin",
      component: Admin,
      hidden: true
    }
  ]
};

const hide = (permission)=>{
  return !hasPermission(Permissions[permission])
}

const hideGeneralRoutes = ()=>{
  return !hasAnyPermission([Permissions['SUPPLIERS'],Permissions['CUSTOMERS'],Permissions['GENERAL_SETTINGS'], Permissions['CUSTOM_FORM_SETTINGS'], Permissions['ALERTS']])
}

const hideProductionRoutes = ()=>{
  return !hasAnyPermission([Permissions['RAW_MATERIAL_SETTINGS'],Permissions['SUPPLY_SETTINGS'],Permissions['FORMULAS'],Permissions['PRODUCTION_SETTINGS'], Permissions['PRODUCT_SETTINGS']])
}

const hideRawMaterials = ()=>{
  return !hasAnyPermission([Permissions['RAW_MATERIAL_RECEPTION'],Permissions['RAW_MATERIAL_STOCK_DECREASE']])
}

const hideSupplies = ()=>{
  return !hasAnyPermission([Permissions['SUPPLY_RECEPTION'],Permissions['SUPPLY_STOCK_DECREASE']])
}

const hideProduction = ()=>{
  return !hasAnyPermission([Permissions['PRODUCTIONS'],Permissions['STARTED_PRODUCTIONS'],Permissions['FINISHED_PRODUCTIONS'], Permissions['IDENTIFICATION_PRODUCTIONS']])
}

const hideProducts = ()=>{
  return !hasAnyPermission([Permissions['PRODUCT_DISTRIBUTION'],Permissions['PRODUCT_STOCK_DECREASE']])
}

const productOrdersEnabled = () => FeatureFlagsService.productOrdersEnabled()

const hideProductOrders = ()=>{
  return productOrdersEnabled() && !hasPermission(Permissions['PRODUCT_ORDERS']) && !hasPermission(Permissions['PRODUCT_ORDER_EXPEDITION'])
}

const hideStock = ()=>{
  return !hasAnyPermission([Permissions['RAW_MATERIAL_STOCK'],Permissions['SUPPLY_STOCK'],Permissions['PRODUCT_STOCK'],Permissions['REPROCESS_STOCK']])
}

const isAdmin = () => LocalStorage.get('isAdmin')

const hideBatchRequests = ()=>{
  return planFeatures.batchRequests() && hide('BATCH_REQUESTS')
}

const hideBatchRequestsFromSidebar = ()=>{
  return hideBatchRequests() || (!planFeatures.batchRequests() && !isAdmin())
}

export const hideConfigurationRoutes = hideGeneralRoutes() && hideProductionRoutes()

export const hideRegisterRoutes = hideStock() && hideProducts() && hideProduction() && hideSupplies() && hideRawMaterials()

export const hideAnalysisRoutes = !hasPermission(Permissions['TRACEABILITY'])

export const generalRoutes = {
  id: getComponentLabels("routes")["general"],
  header: getComponentLabels("routes")["configuration"],
  displayHeader: true,
  path: "/configs",
  icon: <Settings />,
  component: Blank,
  hidden: hideGeneralRoutes(),
  children: [
    {
      path: "/alerts",
      name: getComponentLabels("routes")["alerts"],
      component: Alerts,
      hidden: !hasPermission(Permissions['ALERTS'])
    },
    {
      path: "/customers",
      name: getComponentLabels("routes")["customers"],
      component: Customers,
      hidden: !hasPermission(Permissions['CUSTOMERS'])
    },
    {
      path: "/general_settings",
      name: getComponentLabels("routes")["forms"],
      component: GeneralSettings,
      hidden: !hasPermission(Permissions['GENERAL_SETTINGS'])
    },
    {
      path: "/barcode_label_config",
      name: getComponentLabels("routes")["barcode_label_config"],
      component: BarcodeLabelConfiguration,
      hidden: !isAdmin()
    },
    /*{
      path: "/KPIConfiguration",
      name: "KPIs",
      component: KPIConfiguration,
      hidden: !hasPermission(Permissions['KPI'])
    },*/
    {
      path: "/custom_forms",
      name: getComponentLabels("routes")["custom_forms"],
      component: CustomForms,
      hidden: !hasPermission(Permissions['CUSTOM_FORM_SETTINGS'])
    },
    {
      path: "/suppliers",
      name: getComponentLabels("routes")["suppliers"],
      component: Suppliers,
      hidden: !hasPermission(Permissions['SUPPLIERS'])
    },
    {
      path: "/control_records/configuration",
      name: getComponentLabels("routes")["control_records"],
      component: ControlRecordsConfiguration,
      hidden: !hasPermission(Permissions['CONTROL_RECORDS'], permissionTypes['WRITE'])
    },
  ]
}

export const productionRoutes = {
  id: getComponentLabels("routes")["factory"],
  path: "/configs",
  icon: <Settings />,
  header: getComponentLabels("routes")["configuration"],
  displayHeader: hideGeneralRoutes(),
  hidden: hideProductionRoutes(),
  component: Blank,
  children: [
    {
      path: "/formulas",
      name: getComponentLabels("routes")["formulas"],
      component: Formulas,
      hidden: !hasPermission(Permissions['FORMULAS'])
    },
    {
      path: "/supplies",
      name: getComponentLabels("routes")["supplies"],
      component: Supplies,
      hidden: !hasPermission(Permissions['SUPPLY_SETTINGS'])
    },
    {
      path: "/raw_materials",
      name: getComponentLabels("routes")["raw_materials"],
      component: RawMaterials,
      hidden: !hasPermission(Permissions['RAW_MATERIAL_SETTINGS'])
    },
    {
      path: "/production_configuration",
      name: getComponentLabels("routes")["production"],
      component: ConfigProductions,
      hidden: !hasPermission(Permissions['PRODUCTION_SETTINGS'])
    },
    {
      path: "/products",
      name: getComponentLabels("routes")["products"],
      component: Products,
      hidden: !hasPermission(Permissions['PRODUCT_SETTINGS'])
    },
    {
      path: "/locations",
      name: getComponentLabels("routes")["locations"],
      component: Locations,
      hidden: !hasPermission(Permissions['LOCATION_SETTINGS'])
    },
  ]
};

// Temporary test gate — production planning menus only for this user.
const isProductionPlanningTestUser = () => parseInt(localStorage.getItem('user_id'), 10) === 31;
const isProductionPlanningHidden = () => (
  !isProductionPlanningTestUser()
  || (planIncludes(PLAN_CAPABILITY.PRODUCTION_PLANNING) && !hasPermission(Permissions['PRODUCTION_PLANNING']))
);

export const productionPlanningRoutes = {
  id: getComponentLabels("routes")["production_planning"],
  path: "/production_planning",
  icon: <Calendar />,
  header: getComponentLabels("routes")["configuration"],
  displayHeader: false,
  hidden: isProductionPlanningHidden,
  children: [
    {
      path: "/production_planning/configuration",
      name: getComponentLabels("routes")["production_planning_configuration"],
      component: ProductionPlanningConfiguration,
      hidden: isProductionPlanningHidden,
      planLocked: !planIncludes(PLAN_CAPABILITY.PRODUCTION_PLANNING),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PRO),
    },
  ]
};

export const planificacionesRoutes = {
  id: getComponentLabels("routes")["planificaciones"],
  path: "/planificaciones",
  icon: <Calendar />,
  header: getComponentLabels("routes")["analysis"],
  displayHeader: false,
  hidden: isProductionPlanningHidden,
  children: [
    {
      path: "/planificaciones",
      name: getComponentLabels("routes")["planificaciones"],
      component: Planificaciones,
      hidden: isProductionPlanningHidden,
      planLocked: !planIncludes(PLAN_CAPABILITY.PRODUCTION_PLANNING),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PRO),
    },
  ],
};

const registerRoutes = {
  id: getComponentLabels("routes")["raw_material"],
  header: getComponentLabels("routes")["registers"],
  path: "/register",
  displayHeader: true,
  hidden: hideRawMaterials(),
  icon: <Clipboard />,
  children: [
    {
      path: "/register/reception",
      name: getComponentLabels("routes")["raw_material_reception"],
      component: Reception, 
      hidden: !hasPermission(Permissions['RAW_MATERIAL_RECEPTION'], permissionTypes['WRITE'])
    },
    {
      path: "/receptions",
      name: getComponentLabels("routes")["raw_material_receptions"],
      component: Receptions,
      hidden:!hasPermission(Permissions['RAW_MATERIAL_RECEPTION'])
    },
    {
     path: "/raw_material_stock_decreases",
     name: getComponentLabels("routes")["raw_material_stock_decreases"],
     component: RawMaterialStockDecreases,
     hidden: !hasPermission(Permissions['RAW_MATERIAL_STOCK_DECREASE'])
    },
  ]
};

const getControlRecordsFormChildren = ()=> (
  (LocalStorage.get('ControlRecordsForms') || []).map((form)=> ({
      path: `/control_records/${form.id}`,
      name: form.name,
      component: ControlRecords,
    })
))

const registerControlRecordsRoutes = {
  id: getComponentLabels("routes")["control_records"],
  header: getComponentLabels("routes")["control"],
  path: "/register",
  displayHeader: true,
  hidden: hide('CONTROL_RECORDS'),
  icon: <Clipboard />,
  children: [
    {
     path: "/control_records/productions",
     name: getComponentLabels("routes")["productions"],
     component: ControlRecordsProductions,
     hidden: hide('CONTROL_RECORDS')
    },
    ...getControlRecordsFormChildren()
  ]
};

const registerSupplyRoutes = {
  id: getComponentLabels("routes")["supply"],
  path: "/register",
  header: getComponentLabels("routes")["registers"],
  displayHeader: hideRawMaterials(),
  hidden: hideSupplies(),
  icon: <Clipboard />,
  children: [
    {
      path: "/register/supply",
      name: getComponentLabels("routes")["supply_reception"],
      component: Supply,
      hidden: !hasPermission(Permissions['SUPPLY_RECEPTION'], permissionTypes['WRITE'])
    },
    {
      path: "/supply_receptions",
      name: getComponentLabels("routes")["supply_receptions"],
      component: SupplyReceptions,
      hidden: !hasPermission(Permissions['SUPPLY_RECEPTION'])
    },
    {
      path: "/supply_stock_decreases",
      name: getComponentLabels("routes")["supply_stock_decreases"],
      component: SupplyStockDecreases,
      hidden: !hasPermission(Permissions['SUPPLY_STOCK_DECREASE'])
    },
  ]
};

const registerBatchRequestsRoutes = {
  id: getComponentLabels("routes")["batch_request"],
  path: "/register",
  header: getComponentLabels("routes")["orders_header"],
  displayHeader: true,
  hidden: hideBatchRequests(),
  icon: <Clipboard />,
  children: [
    {
      path: "/register/batch_request",
      name: getComponentLabels("routes")["batch_request_reception"],
      component: BatchRequest,
      hidden: planFeatures.batchRequests() && !hasPermission(Permissions['BATCH_REQUESTS'], permissionTypes['WRITE']),
      planLocked: !planFeatures.batchRequests(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.FULL),
    },
    {
      path: "/batch_requests",
      name: getComponentLabels("routes")["batch_request_receptions"],
      component: BatchRequests,
      hidden: planFeatures.batchRequests() && hide('BATCH_REQUESTS'),
      planLocked: !planFeatures.batchRequests(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.FULL),
    }
  ]
};

const registerProductionRoutes = {
  id: getComponentLabels("routes")["production_id"],
  path: "/register",
  header: getComponentLabels("routes")["registers"],
  displayHeader: hideRawMaterials() && hideSupplies(),
  hidden: hideProduction(),
  icon: <Clipboard />,
  children: [
    {
      path: "/production",
      name: getComponentLabels("routes")["new_production"],
      component: RegisterProduction,
      hidden: !hasPermission(Permissions['PRODUCTIONS'])
    },
    {
      path: "/started_productions",
      name: getComponentLabels("routes")["started_productions"],
      component: Productions,
      hidden: !hasAnyPermission([Permissions['STARTED_PRODUCTIONS'], Permissions['PRODUCTIONS']])
    },
    {
      path: "/identification_productions",
      name: getComponentLabels("routes")["identification_productions"],
      component: Productions,
      hidden: !hasAnyPermission([Permissions['IDENTIFICATION_PRODUCTIONS'], Permissions['PRODUCTIONS']])
    },
    {
      path: "/finished_productions",
      name: getComponentLabels("routes")["finished_productions"],
      component: Productions,
      hidden: !hasPermission(Permissions['FINISHED_PRODUCTIONS'])
    },
    {
      path: "/costs",
      name: getComponentLabels("routes")["costs"],
      component: Costs,
      hidden: planFeatures.costs() && (!hasPermission(Permissions['FINISHED_PRODUCTIONS']) || !hasPermission(Permissions['COST_RECORDS'])),
      planLocked: !planFeatures.costs(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PYME),
    },
    {
      path: "/register/procedure",
      component: Procedure,
      hidden: true
    },
    {
      path: "/register/final_step",
      name: "Paso final",
      component: FinalProductionStep,
      hidden: true
    },
  ]
}

const registerProductOrdersRoutes = {
  id: getComponentLabels("routes")["orders"],
  path: "/register",
  header: getComponentLabels("routes")["orders_header"],
  displayHeader: hideBatchRequestsFromSidebar(),
  hidden: hideProductOrders(),
  icon: <Clipboard />,
  children: [
    {
      path: "/register/product_order",
      name: getComponentLabels("routes")["product_order"],
      component: ProductOrder,
      hidden: productOrdersEnabled() && !hasPermission(Permissions['PRODUCT_ORDERS'], permissionTypes['WRITE']),
      planLocked: !productOrdersEnabled(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.FULL),
    },
    {
      path: "/product_orders",
      name: getComponentLabels("routes")["product_orders"],
      component: ProductOrders,
      hidden: productOrdersEnabled() && !hasPermission(Permissions['PRODUCT_ORDERS']) && !hasPermission(Permissions['PRODUCT_ORDER_EXPEDITION']),
      planLocked: !productOrdersEnabled(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.FULL),
    },
  ]
}

const registerDistributionRoutes = {
  id: getComponentLabels("routes")["product"],
  path: "/register",
  header: getComponentLabels("routes")["registers"],
  displayHeader: hideRawMaterials() && hideSupplies() && hideProduction(),
  hidden: hideProducts(),
  icon: <Clipboard />,
  children: [
    {
      path: "/register/distribution",
      name: getComponentLabels("routes")["product_distribution"],
      component: Distribution,
      hidden: !hasPermission(Permissions['PRODUCT_DISTRIBUTION'], permissionTypes['WRITE'])
    },
    {
      path: "/distributions",
      name: getComponentLabels("routes")["product_distributions"],
      component: Distributions,
      hidden: !hasPermission(Permissions['PRODUCT_DISTRIBUTION'])
    },
    {
      path: "/product_stock_additions",
      name: getComponentLabels("routes")["stock_additions"],
      component: ProductReceptions,
      hidden: !hasPermission(Permissions['PRODUCT_STOCK_DECREASE'])
    },
    {
      path: "/product_stock_decreases",
      name: getComponentLabels("routes")["product_stock_decreases"],
      component: ProductStockDecreases,
      hidden: !hasPermission(Permissions['PRODUCT_STOCK_DECREASE'])
    }
  ]
}

const registerStockRoutes = {
  id: getComponentLabels("routes")["stock"],
  path: "/register",
  header: getComponentLabels("routes")["registers"],
  displayHeader: hideRawMaterials() && hideSupplies() && hideProduction() && hideProducts(),
  hidden: hideStock(),
  icon: <Clipboard />,
  children: [
    {
      path: "/raw_material_stock",
      name: getComponentLabels("routes")["raw_material_stock"],
      component: RawMaterialBatches,
      hidden:!hasPermission(Permissions['RAW_MATERIAL_STOCK'])
    },
    {
      path: "/supply_stock",
      name: getComponentLabels("routes")["supply_stock"],
      component: SupplyBatches,
      hidden: !hasPermission(Permissions['SUPPLY_STOCK']),
    },
    {
      path: "/product_batches_stock",
      name: getComponentLabels("routes")["product_batches_stock"],
      component: ProductBatches,
      hidden: !hasPermission(Permissions['PRODUCT_STOCK']),
    },
    {
      path: "/reprocesses_stock",
      name: getComponentLabels("routes")["reprocesses_stock"],
      component: ReprocessesBatches,
      hidden: !hasPermission(Permissions['REPROCESS_STOCK']),
    },
    {
      path: "/reprocess_stock_decreases",
      name: getComponentLabels("routes")["reprocesses_stock_decreases"],
      component: ReprocessesStockDecreases,
      hidden: true,
    },
  ]
}

const getCustomFormChildren = () => {
  const customForms = LocalStorage.get('customForms') || [];

  if (customForms.length === 0) {
    return [{
      path: '/custom_forms',
      name: getComponentLabels('routes')['custom_forms'],
      component: CustomForms,
    }];
  }

  return customForms.map((form) => ({
    path: `/forms/${form.id}`,
    name: form.table_name,
    component: CustomForm,
  }));
};

const registerCustomFormRoutes = {
  id: getComponentLabels("routes")["custom_form_id"],
  path: "/register",
  header: getComponentLabels("routes")["control"],
  displayHeader: hide('CONTROL_RECORDS'),
  hidden: !hasPermission(Permissions['CUSTOM_FORMS']),
  icon: <Clipboard />,
  children: getCustomFormChildren()
}

const nonconformitiesRoutes = {
  id: getComponentLabels("routes")["nonconformities"],
  path: "/register",
  header: getComponentLabels("routes")["control"],
  displayHeader: hide('CONTROL_RECORDS') && !hasPermission(Permissions['CUSTOM_FORMS']),
  hidden: planIncludes(PLAN_CAPABILITY.NONCONFORMITY) && !hasPermission(Permissions['NONCONFORMITY']),
  icon: <Clipboard />,
  children: [
    {
      path: "/nonconformities",
      name: getComponentLabels("routes")["all_nonconformities"],
      component: AllNonconformities,
      hidden: !isAdmin(),
      planLocked: !planIncludes(PLAN_CAPABILITY.NONCONFORMITY),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PYME),
    },
    {
      path: "/received_nonconformities",
      name: getComponentLabels("routes")["received_nonconformities"],
      component: Nonconformities,
      hidden: planIncludes(PLAN_CAPABILITY.NONCONFORMITY) && !hasPermission(Permissions['NONCONFORMITY']),
      planLocked: !planIncludes(PLAN_CAPABILITY.NONCONFORMITY),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PYME),
    },
    {
      path: "/sent_nonconformities",
      name: getComponentLabels("routes")["sent_nonconformities"],
      component: Nonconformities,
      hidden: planIncludes(PLAN_CAPABILITY.NONCONFORMITY) && !hasPermission(Permissions['NONCONFORMITY'], permissionTypes['WRITE']),
      planLocked: !planIncludes(PLAN_CAPABILITY.NONCONFORMITY),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PYME),
    },
  ]
}

const traceabilityRoutes = {
  id: getComponentLabels("routes")["traceability"],
  path: "/traceability",
  header: getComponentLabels("routes")["analysis"],
  displayHeader: hide('KPI'),
  icon: <Sliders />,
  component: Default,
  hidden: true,
};

const traceabilityV2Routes = {
  id: `${getComponentLabels("routes")["traceability"]}`,
  path: "/traceability_v2",
  header: getComponentLabels("routes")["analysis"],
  displayHeader: false,
  icon: <Sliders />,
  component: DefaultV2,
  hidden: !hasPermission(Permissions['TRACEABILITY']),
};

const KPIRoutes = {
  id: getComponentLabels("routes")["kpi"],
  path: "/KPI",
  header: getComponentLabels("routes")["analysis"],
  displayHeader: true,
  hidden: false,
  icon: <Sliders />,
  children: [
    {
      path: "/KPI/stock",
      name: getComponentLabels("routes")["kpi_stock"],
      component: KPIStock,
      hidden: false,
      planLocked: !planFeatures.stockKpi(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.FULL),
    },
    {
      path: "/KPI/productions",
      name: getComponentLabels("routes")["kpi_productions"],
      component: KPIProductions,
      hidden: planFeatures.statsKpis() && !hasPermission(Permissions['KPI']),
      planLocked: !planFeatures.statsKpis(),
      lockedTooltip: getPlanAvailableFromTooltip(PLAN_NAMES.PYME),
    }
  ]
}

const landingRoutes = {
  id: "",
  path: "/",
  displayHeader: false,
  component: Landing,
  hidden: true
};

const subscriptionRoutes = {
  id: getComponentLabels("Header")["my_subscription"],
  path: "/subscription",
  displayHeader: false,
  component: Subscription,
  hidden: true
};

const homeRoutes = {
  id: "",
  path: "/home",
  displayHeader: false,
  component: Home,
  hidden: true
};

const NotificationsRoutes = {
  id: getComponentLabels("routes")["notifications"],
  path: "/notifications",
  displayHeader: false,
  component: Notifications,
  hidden: true
};

const contactRoutes = {
  id: getComponentLabels("routes")["contact_us"],
  path: "/contact_us",
  icon: <Mail />,
  component: ContactUs,
};

const MyCloudRoutes = {
  id: getComponentLabels("routes")["my_cloud"],
  path: "/my_cloud",
  icon: <UploadCloud />,
  component: MyCloud,
  displayHeader:true,
  hidden: !hasPermission(Permissions['CLOUD_STORAGE']),
};

const tutorialRoutes ={
  id: getComponentLabels("routes")["tutorials"],
  path: "https://trazal.com.ar/manual-del-usuario-trazal-software-de-gestion-control-de-stock-y-trazabilidad-alimentaria/",
  icon: <Video />,
  hidden: false,
  externalURL: true,
  displayHeader: !hasPermission(Permissions['CLOUD_STORAGE'])
}

export const generalPublicRoutes = {
  id: "public_routes",
  path: "/public_routes",
  // icon: <Users />,
  children: [
    {
      id: "Implementation",
      path:"/empresa/:id",
      displayHeader: false,
      component: Implementation,
      hidden: true
    }
  ]
};

export const originalMenu = [
  userRoutes,
  generalRoutes,
  productionRoutes,
  productionPlanningRoutes,
  registerRoutes,
  registerSupplyRoutes,
  registerProductionRoutes,
  registerDistributionRoutes,
  registerStockRoutes,
  registerBatchRequestsRoutes,
  registerProductOrdersRoutes,
  registerControlRecordsRoutes,
  registerCustomFormRoutes,
  nonconformitiesRoutes,
  KPIRoutes,
  planificacionesRoutes,
  traceabilityRoutes,
  traceabilityV2Routes,
  MyCloudRoutes,
  tutorialRoutes,
  landingRoutes,
  subscriptionRoutes,
  homeRoutes,
  contactRoutes,
  NotificationsRoutes
];
export const auth = [authRoutes];
export const publicRoutes = [generalPublicRoutes];
export default originalMenu;

